import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Search, Plus, Trash2, Receipt, UserPlus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function NewBill() {
  const navigate = useNavigate();
  const token = localStorage.getItem('vendor_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [centres, setCentres] = useState([]);
  const [agents, setAgents] = useState([]);
  const [processingLabs, setProcessingLabs] = useState([]);
  const [testSearch, setTestSearch] = useState('');
  const [testResults, setTestResults] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ referred_by: '', collection_centre: '', sample_collection_agent: '', processing_lab: '', discount: 0, amount_received: 0, payment_mode: 'cash', remarks: '' });
  const [saving, setSaving] = useState(false);

  // Add Patient Dialog
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [patientForm, setPatientForm] = useState({ title: '', first_name: '', last_name: '', sex: '', age_years: '', age_months: '', age_days: '', mobile: '', email: '', address: '', aadhaar: '' });
  const [savingPatient, setSavingPatient] = useState(false);

  // Store full test data for B2B recalculation
  const testDataRef = useRef({});

  const fetchMeta = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    const [dRes, cRes, aRes, lRes] = await Promise.all([
      fetch(`${API}/api/lis/doctors`, { headers: h }),
      fetch(`${API}/api/lis/centres`, { headers: h }),
      fetch(`${API}/api/lis/agents`, { headers: h }),
      fetch(`${API}/api/lis/processing-labs`, { headers: h })
    ]);
    setDoctors(await dRes.json());
    setCentres(await cRes.json());
    setAgents(await aRes.json());
    setProcessingLabs(await lRes.json());
  }, [token]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  // Recalculate B2B prices when processing lab changes
  useEffect(() => {
    if (items.length === 0) return;
    setItems(prev => prev.map(item => {
      const testData = testDataRef.current[item.item_id];
      if (!testData) return item;
      const labMatch = form.processing_lab ? (testData.b2b_prices || []).find(p => p.lab === form.processing_lab) : null;
      const b2bPrice = form.processing_lab ? (labMatch?.price ?? testData.b2b_price ?? 0) : (testData.b2b_price ?? 0);
      return { ...item, b2b_price: b2bPrice, has_lab_rate: !form.processing_lab || !!labMatch };
    }));
  }, [form.processing_lab]);

  const searchPatients = async (q) => {
    setPatientSearch(q);
    if (q.length < 2) { setPatientResults([]); return; }
    const res = await fetch(`${API}/api/lis/patients/search?mobile=${q}`, { headers: { Authorization: `Bearer ${token}` } });
    setPatientResults(await res.json());
  };

  const searchTests = async (q) => {
    setTestSearch(q);
    if (q.length < 2) { setTestResults([]); return; }
    const [testRes, panelRes] = await Promise.all([
      fetch(`${API}/api/lis/tests/search?q=${q}`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`${API}/api/lis/panels/search?q=${q}`, { headers: { Authorization: `Bearer ${token}` } })
    ]);
    const tests = (await testRes.json()).map(t => ({ ...t, item_type: 'test' }));
    const panels = (await panelRes.json()).map(p => ({ ...p, item_type: 'panel' }));
    setTestResults([...panels, ...tests]);
  };

  const selectPatient = (p) => {
    setSelectedPatient(p);
    setPatientSearch('');
    setPatientResults([]);
  };

  const addItem = (item) => {
    if (items.find(i => i.item_id === item.id)) return;
    // Store full item data (test or panel) for later B2B recalculation
    testDataRef.current[item.id] = item;
    const labMatch = form.processing_lab ? (item.b2b_prices || []).find(p => p.lab === form.processing_lab) : null;
    const b2bPrice = form.processing_lab ? (labMatch?.price ?? item.b2b_price ?? 0) : (item.b2b_price ?? 0);
    setItems(prev => [...prev, { item_id: item.id, item_name: item.name, item_type: item.item_type || 'test', price: item.price || 0, b2b_price: b2bPrice, has_lab_rate: !form.processing_lab || !!labMatch }]);
    setTestSearch('');
    setTestResults([]);
  };

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const updateItemPrice = (idx, field, val) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: parseFloat(val) || 0 } : item));
  };

  const total = items.reduce((s, i) => s + i.price, 0);
  const totalB2B = items.reduce((s, i) => s + i.b2b_price, 0);
  const balance = total - (parseFloat(form.discount) || 0) - (parseFloat(form.amount_received) || 0);

  // Add Patient inline
  const openAddPatient = () => {
    setPatientForm({ title: '', first_name: '', last_name: '', sex: '', age_years: '', age_months: '', age_days: '', mobile: patientSearch || '', email: '', address: '', aadhaar: '' });
    setShowAddPatient(true);
  };

  const saveNewPatient = async () => {
    if (!patientForm.first_name.trim()) { toast.error('First name is required'); return; }
    setSavingPatient(true);
    try {
      const res = await fetch(`${API}/api/lis/patients`, { method: 'POST', headers, body: JSON.stringify(patientForm) });
      if (res.ok) {
        const patient = await res.json();
        selectPatient(patient);
        setShowAddPatient(false);
        toast.success('Patient created & selected');
      } else { toast.error('Failed to create patient'); }
    } finally { setSavingPatient(false); }
  };

  const createBill = async () => {
    if (!selectedPatient) { toast.error('Select a patient'); return; }
    if (items.length === 0) { toast.error('Add at least one test'); return; }
    setSaving(true);
    try {
      const body = {
        patient_id: selectedPatient.id,
        patient_name: [selectedPatient.title, selectedPatient.first_name, selectedPatient.last_name].filter(Boolean).join(' '),
        patient_mobile: selectedPatient.mobile || '',
        items,
        total,
        total_b2b: totalB2B,
        discount: parseFloat(form.discount) || 0,
        amount_received: parseFloat(form.amount_received) || 0,
        payment_mode: form.payment_mode,
        referred_by: form.referred_by,
        collection_centre: form.collection_centre,
        sample_collection_agent: form.sample_collection_agent,
        processing_lab: form.processing_lab,
        remarks: form.remarks
      };
      const res = await fetch(`${API}/api/lis/bills`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (res.ok) {
        const bill = await res.json();
        toast.success(`Bill #${bill.reg_no} created`);
        navigate(`/vendor/lis/bill/${bill.id}`);
      } else {
        toast.error('Failed to create bill');
      }
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="lis-new-bill" className="max-w-4xl">
      <h1 className="text-xl font-bold text-slate-800 mb-4">New Bill</h1>
      <div className="grid gap-4 md:grid-cols-2">
        {/* Patient Selection */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Patient</CardTitle>
              {!selectedPatient && (
                <Button variant="outline" size="sm" onClick={openAddPatient} data-testid="add-new-patient-btn" className="h-7 text-xs">
                  <UserPlus className="w-3 h-3 mr-1" /> New Patient
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {selectedPatient ? (
              <div className="p-3 bg-blue-50 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{[selectedPatient.title, selectedPatient.first_name, selectedPatient.last_name].filter(Boolean).join(' ')}</p>
                  <p className="text-xs text-slate-500">{selectedPatient.mobile} | #{selectedPatient.patient_id}</p>
                </div>
                <button onClick={() => setSelectedPatient(null)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                <Input placeholder="Search by mobile or name..." value={patientSearch} onChange={e => searchPatients(e.target.value)} className="pl-9 h-9" data-testid="patient-search-input" />
                {patientResults.length > 0 && (
                  <div className="absolute top-10 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto">
                    {patientResults.map(p => (
                      <button key={p.id} onClick={() => selectPatient(p)} className="w-full text-left p-2 hover:bg-slate-50 text-sm border-b last:border-0">
                        {[p.title, p.first_name, p.last_name].filter(Boolean).join(' ')} - {p.mobile}
                      </button>
                    ))}
                  </div>
                )}
                {patientSearch.length >= 2 && patientResults.length === 0 && (
                  <div className="absolute top-10 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg z-10 p-3 text-center">
                    <p className="text-xs text-slate-500 mb-2">No patients found</p>
                    <Button variant="outline" size="sm" onClick={openAddPatient} className="h-7 text-xs" data-testid="add-patient-from-search">
                      <UserPlus className="w-3 h-3 mr-1" /> Create New Patient
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bill Details */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3"><CardTitle className="text-sm">Details</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <Select value={form.referred_by} onValueChange={v => setForm(f => ({ ...f, referred_by: v }))}>
              <SelectTrigger className="h-9"><SelectValue placeholder="Referred By (Doctor)" /></SelectTrigger>
              <SelectContent>{doctors.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.collection_centre} onValueChange={v => setForm(f => ({ ...f, collection_centre: v }))}>
              <SelectTrigger className="h-9"><SelectValue placeholder="Collection Centre" /></SelectTrigger>
              <SelectContent>{centres.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-2">
              <Select value={form.sample_collection_agent} onValueChange={v => setForm(f => ({ ...f, sample_collection_agent: v }))}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Agent" /></SelectTrigger>
                <SelectContent>{agents.map(a => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.processing_lab} onValueChange={v => setForm(f => ({ ...f, processing_lab: v }))}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Processing Lab" /></SelectTrigger>
                <SelectContent>{processingLabs.map(l => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Test Selection */}
      <Card className="mt-4 border-slate-200">
        <CardHeader className="pb-3"><CardTitle className="text-sm">Tests / Panels</CardTitle></CardHeader>
        <CardContent>
          <div className="relative mb-3">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
            <Input placeholder="Search tests & panels to add..." value={testSearch} onChange={e => searchTests(e.target.value)} className="pl-9 h-9" data-testid="test-search-input" />
            {testResults.length > 0 && (
              <div className="absolute top-10 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                {testResults.map(t => (
                  <button key={t.id} onClick={() => addItem(t)} className="w-full text-left p-2 hover:bg-slate-50 text-sm border-b last:border-0 flex justify-between items-center" data-testid={`add-item-${t.id}`}>
                    <span>{t.name} {t.item_type === 'panel' && <span className="text-[10px] text-blue-500 border border-blue-200 rounded px-1 ml-1">PANEL</span>}</span>
                    <span className="text-slate-500">Rs.{t.price}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {items.length > 0 ? (
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-slate-50"><th className="p-2 text-left">Test</th><th className="p-2 text-right w-28">MRP</th><th className="p-2 text-right w-28">B2B</th><th className="p-2 w-10"></th></tr></thead>
              <tbody>
                {items.map((item, i) => (
                  <tr key={`row-${i}`} className="border-b border-slate-100">
                    <td className="p-2">{item.item_name}</td>
                    <td className="p-2"><Input type="number" value={item.price} onChange={e => updateItemPrice(i, 'price', e.target.value)} className="h-7 text-right w-24 ml-auto" /></td>
                    <td className="p-2 text-right">
                      <span className="text-blue-600" data-testid={`item-b2b-price-${i}`}>Rs.{item.b2b_price}</span>
                      {form.processing_lab && !item.has_lab_rate && (
                        <span className="block text-[10px] text-amber-500" title={`No B2B rate configured for ${form.processing_lab} - using this test's default rate`} data-testid={`item-b2b-default-warning-${i}`}>
                          default rate
                        </span>
                      )}
                    </td>
                    <td className="p-2"><button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">Search and add tests above</p>
          )}
        </CardContent>
      </Card>

      {/* Payment */}
      <Card className="mt-4 border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <div><label className="text-xs text-slate-500">Total</label><p className="text-lg font-bold">Rs.{total}</p></div>
            <div><label className="text-xs text-slate-500">Discount</label><Input type="number" value={form.discount} onChange={e => setForm(f => ({ ...f, discount: e.target.value }))} className="h-9" /></div>
            <div><label className="text-xs text-slate-500">Received</label><Input type="number" value={form.amount_received} onChange={e => setForm(f => ({ ...f, amount_received: e.target.value }))} className="h-9" /></div>
            <Select value={form.payment_mode} onValueChange={v => setForm(f => ({ ...f, payment_mode: v }))}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="bank_transfer">Bank Transfer</SelectItem></SelectContent>
            </Select>
            <div><label className="text-xs text-slate-500">Balance</label><p className={`text-lg font-bold ${balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>Rs.{Math.max(0, balance)}</p></div>
          </div>
          <Input placeholder="Remarks (optional)" value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} className="mt-3 h-9" />
          <Button className="w-full mt-4" onClick={createBill} disabled={saving} data-testid="create-bill-btn">
            <Receipt className="w-4 h-4 mr-2" /> {saving ? 'Creating...' : 'Create Bill'}
          </Button>
        </CardContent>
      </Card>

      {/* Add Patient Dialog */}
      <Dialog open={showAddPatient} onOpenChange={setShowAddPatient}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="add-patient-dialog">
          <DialogHeader><DialogTitle>Add New Patient</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Select value={patientForm.title} onValueChange={v => setPatientForm(f => ({ ...f, title: v }))}>
                <SelectTrigger><SelectValue placeholder="Title" /></SelectTrigger>
                <SelectContent><SelectItem value="Mr.">Mr.</SelectItem><SelectItem value="Mrs.">Mrs.</SelectItem><SelectItem value="Ms.">Ms.</SelectItem><SelectItem value="Dr.">Dr.</SelectItem><SelectItem value="Baby">Baby</SelectItem></SelectContent>
              </Select>
              <Input placeholder="First Name *" value={patientForm.first_name} onChange={e => setPatientForm(f => ({ ...f, first_name: e.target.value }))} data-testid="new-patient-fname" />
              <Input placeholder="Last Name" value={patientForm.last_name} onChange={e => setPatientForm(f => ({ ...f, last_name: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select value={patientForm.sex} onValueChange={v => setPatientForm(f => ({ ...f, sex: v }))}>
                <SelectTrigger><SelectValue placeholder="Sex" /></SelectTrigger>
                <SelectContent><SelectItem value="MALE">Male</SelectItem><SelectItem value="FEMALE">Female</SelectItem></SelectContent>
              </Select>
              <Input placeholder="Mobile" value={patientForm.mobile} onChange={e => setPatientForm(f => ({ ...f, mobile: e.target.value }))} data-testid="new-patient-mobile" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Input type="number" placeholder="Years" value={patientForm.age_years} onChange={e => setPatientForm(f => ({ ...f, age_years: e.target.value }))} />
              <Input type="number" placeholder="Months" value={patientForm.age_months} onChange={e => setPatientForm(f => ({ ...f, age_months: e.target.value }))} />
              <Input type="number" placeholder="Days" value={patientForm.age_days} onChange={e => setPatientForm(f => ({ ...f, age_days: e.target.value }))} />
            </div>
            <Input placeholder="Email" value={patientForm.email} onChange={e => setPatientForm(f => ({ ...f, email: e.target.value }))} />
            <Input placeholder="Address" value={patientForm.address} onChange={e => setPatientForm(f => ({ ...f, address: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPatient(false)}>Cancel</Button>
            <Button onClick={saveNewPatient} disabled={savingPatient} data-testid="save-new-patient-btn">
              {savingPatient ? 'Creating...' : 'Create & Select'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
